import React, { useMemo, useState } from 'react';
import {
    CheckCircle2, ChevronDown, ChevronUp, Circle, CircleAlert, Clock, ExternalLink, Handshake, Loader2,
} from 'lucide-react';
import { avisosDoAcordo, type AcordoParaAvisos } from '../../../src/lib/entradaAcordoRegras.ts';
import {
    ordenarAcordos, resumirAcordo, type Etiqueta, type ParcelaResumida, type ResumoAcordo, type Tom,
} from '../../../src/lib/situacaoAcordo.ts';
import type { AcordosDaParada as DadosAcordos } from '../../../src/lib/rotaCobranca';
import { COR_TOM } from './corTom';

/**
 * Acordos de parcelamento da parada, no card da rota de cobrança.
 *
 * Pedido de 30/09/2026: na porta do cliente, saber se já existe acordo, se o
 * termo foi assinado, se a entrada foi paga e como estão as parcelas — sem abrir
 * a tela de Parcelamentos em outra aba. Cada acordo vivo vira uma ficha com as
 * três condições em linhas de check; os encerrados ficam numa linha só.
 *
 * Nenhuma regra mora aqui: as frases e as cores saem de `situacaoAcordo.ts`.
 */

const ICONE: Record<Tom, React.FC<{ size?: number; className?: string }>> = {
    ok: CheckCircle2,
    atencao: Clock,
    perigo: CircleAlert,
    info: Circle,
    neutro: Circle,
};

const TOM_PARCELA: Record<ParcelaResumida['situacao'], Tom> = {
    paga: 'ok',
    vencida: 'perigo',
    a_vencer: 'neutro',
    prevista: 'neutro',
    cancelada: 'neutro',
};

const ROTULO_PARCELA: Record<ParcelaResumida['situacao'], string> = {
    paga: 'Paga',
    vencida: 'Vencida',
    a_vencer: 'A vencer',
    prevista: 'Prevista',
    cancelada: 'Cancelada',
};

const dataBr = (iso: string | null | undefined) => {
    if (!iso) return '—';
    const [a, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
};

const moeda = (v: number | null | undefined) =>
    (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Tela de Parcelamentos já filtrada neste acordo — o que o cobrador abriria na mão. */
const linkParcelamentos = (codigo: string) =>
    `#/financeiro/parcelamentos?busca=${encodeURIComponent(codigo)}&status=todos`;

const LinhaCondicao: React.FC<{ etiqueta: Etiqueta }> = ({ etiqueta }) => {
    const Icone = ICONE[etiqueta.tom];
    return (
        <li className="flex items-start gap-1.5">
            <Icone size={13} className={`shrink-0 mt-px ${COR_TOM[etiqueta.tom].icone}`} />
            <span className={`text-[11px] leading-snug ${COR_TOM[etiqueta.tom].texto}`}>{etiqueta.rotulo}</span>
        </li>
    );
};

/** Uma barrinha por parcela, na ordem: verde paga, vermelha vencida, cinza a vencer.
 *  Lê-se de relance "quantas já foram e onde travou"; o detalhe abre embaixo. */
const TrilhoParcelas: React.FC<{ parcelas: ParcelaResumida[]; resumo: string }> = ({ parcelas, resumo }) => {
    const [aberto, setAberto] = useState(false);
    const validas = parcelas.filter(p => p.situacao !== 'cancelada');
    if (validas.length === 0) return null;
    return (
        <div className="space-y-1.5">
            <div className="flex gap-0.5" role="img" aria-label={resumo}>
                {validas.map(p => (
                    <span
                        key={p.numero}
                        title={`Parcela ${p.numero} · ${dataBr(p.vencimento)} · ${moeda(p.valor)} · ${ROTULO_PARCELA[p.situacao]}`}
                        className={`h-1.5 flex-1 rounded-full ${COR_TOM[TOM_PARCELA[p.situacao]].trilho}`}
                    />
                ))}
            </div>
            <button
                type="button"
                onClick={() => setAberto(v => !v)}
                aria-expanded={aberto}
                className="flex items-center gap-1 text-[10.5px] font-semibold text-gray-500 hover:text-[#5F6C37] transition-colors"
            >
                {aberto ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {aberto ? 'Esconder as parcelas' : `Ver as ${validas.length} parcelas`}
            </button>
            {aberto ? (
                <ol className="rounded-md border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                    {validas.map(p => (
                        <li key={p.numero} className="flex items-center gap-2 px-2 py-1 text-[10.5px] tabular-nums">
                            <span className="w-5 font-bold text-gray-500">{String(p.numero).padStart(2, '0')}</span>
                            <span className="text-gray-600 dark:text-gray-300">{dataBr(p.vencimento)}</span>
                            <span className="flex-1 text-right font-semibold text-gray-800 dark:text-gray-100">{moeda(p.valor)}</span>
                            <span className={`w-24 text-right font-bold ${COR_TOM[TOM_PARCELA[p.situacao]].icone}`}>
                                {p.situacao === 'paga' && p.pagaEm ? `Paga ${dataBr(p.pagaEm).slice(0, 5)}` : ROTULO_PARCELA[p.situacao]}
                            </span>
                        </li>
                    ))}
                </ol>
            ) : null}
        </div>
    );
};

const FichaAcordo: React.FC<{ resumo: ResumoAcordo; avisos: ReturnType<typeof avisosDoAcordo> }> = ({ resumo: r, avisos }) => (
    <div className={`p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${COR_TOM[r.tom].faixa} bg-white dark:bg-gray-800 space-y-2`}>
        <div className="flex items-start justify-between gap-2">
            <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-gray-700 dark:text-gray-200">
                <Handshake size={12} className="shrink-0" />
                <span>Acordo de parcelamento <span className="font-mono normal-case tracking-normal text-gray-400">#{r.codigo}</span></span>
            </p>
            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] leading-none font-black ring-1 ring-inset ${COR_TOM[r.tom].chip}`}>
                {r.rotuloStatus}
            </span>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
            {r.total != null ? <><strong className="text-gray-800 dark:text-gray-100">{moeda(r.total)}</strong> no total · </> : null}
            criado em {dataBr(r.criadoEm)}
        </p>
        {r.pendente ? (
            <p className="text-[10.5px] leading-snug text-amber-800 dark:text-amber-200">
                Ainda não vale: as faturas antigas continuam vencidas e com boleto até o cliente assinar
                {' '}<strong>e</strong> pagar a entrada.
            </p>
        ) : null}
        <ul className="space-y-1">
            <LinhaCondicao etiqueta={r.termo} />
            <LinhaCondicao etiqueta={r.entrada} />
            {r.parcelas.rotulo ? <LinhaCondicao etiqueta={r.parcelas} /> : null}
        </ul>
        {!r.pendente ? <TrilhoParcelas parcelas={r.detalheParcelas} resumo={r.parcelas.rotulo} /> : null}
        {avisos.map(a => (
            <p key={a.titulo} className={`text-[10.5px] leading-snug ${a.nivel === 'erro' ? COR_TOM.perigo.texto : COR_TOM.atencao.texto}`}>
                <strong>{a.titulo}.</strong> {a.detalhe}
            </p>
        ))}
        <a
            href={linkParcelamentos(r.codigo)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#5F6C37] dark:text-[#9fb567] hover:underline"
        >
            Abrir em Parcelamentos <ExternalLink size={11} />
        </a>
    </div>
);

const AcordosDaParada: React.FC<{
    /** null = ainda lendo. */
    dados: DadosAcordos | null;
    erro: string | null;
}> = ({ dados, erro }) => {
    const { vivos, anteriores } = useMemo(() => {
        if (!dados) return { vivos: [], anteriores: [] };
        const hoje = new Date();
        const todos = ordenarAcordos(dados.acordos).map(a => ({
            resumo: resumirAcordo(a, dados.faturas, hoje),
            avisos: avisosDoAcordo({
                ...(a as unknown as AcordoParaAvisos),
                entrada: a.entrada_fatura_id != null
                    ? (dados.faturas.get(String(a.entrada_fatura_id)) as AcordoParaAvisos['entrada']) ?? null
                    : null,
            }),
        }));
        return {
            vivos: todos.filter(t => t.resumo.vivo),
            anteriores: todos.filter(t => !t.resumo.vivo).slice(0, 3).map(t => t.resumo),
        };
    }, [dados]);

    if (erro) {
        return <p className="text-[11px] text-gray-400">Não consegui ler os acordos desta UC ({erro}).</p>;
    }
    if (!dados) {
        return (
            <p className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <Loader2 size={12} className="animate-spin" /> Procurando acordo de parcelamento…
            </p>
        );
    }
    if (vivos.length === 0 && anteriores.length === 0) {
        return (
            <p className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                <Handshake size={12} className="shrink-0" /> Sem acordo de parcelamento nesta UC.
            </p>
        );
    }
    return (
        <div className="space-y-2">
            {vivos.map(v => <FichaAcordo key={v.resumo.id} resumo={v.resumo} avisos={v.avisos} />)}
            {anteriores.length > 0 ? (
                <ul className="space-y-0.5">
                    {anteriores.map(a => (
                        <li key={a.id} className="flex items-center gap-1.5 text-[10.5px] text-gray-500 dark:text-gray-400">
                            <Handshake size={11} className="shrink-0" />
                            <span>
                                Acordo <span className="font-mono">#{a.codigo}</span> {a.rotuloStatus.toLowerCase()}
                                {a.encerradoEm ? ` em ${dataBr(a.encerradoEm)}` : ''}
                                {a.status === 'concluido' ? ' — todas as parcelas pagas' : ''}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

export default AcordosDaParada;
